import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  Users, 
  MessageSquare, 
  FileText, 
  Trophy,
  Building,
  GraduationCap,
  Plus
} from 'lucide-react';

interface QuickAction {
  title: string;
  description: string;
  icon: React.ReactNode;
  action: () => void;
  variant?: 'default' | 'secondary' | 'outline';
}

interface QuickActionCardsProps {
  userRole: 'coach' | 'team-owner' | 'venue';
  onCreateTeam?: () => void;
  onManagePlayers?: () => void;
  onScheduleGame?: () => void;
  onPostLesson?: () => void;
  onCreateEvent?: () => void;
  onManageBookings?: () => void;
}

export const QuickActionCards: React.FC<QuickActionCardsProps> = ({
  userRole,
  onCreateTeam,
  onManagePlayers,
  onScheduleGame,
  onPostLesson,
  onCreateEvent,
  onManageBookings
}) => {
  const navigate = useNavigate();

  const coachActions: QuickAction[] = [
    {
      title: 'Create Team',
      description: 'Set up a new team',
      icon: <Users className="h-5 w-5" />,
      action: () => onCreateTeam?.(),
      variant: 'default'
    },
    {
      title: 'Post Lesson',
      description: 'Share coaching content',
      icon: <GraduationCap className="h-5 w-5" />,
      action: () => onPostLesson?.(),
      variant: 'secondary'
    },
    {
      title: 'Schedule Game',
      description: 'Add upcoming game',
      icon: <Calendar className="h-5 w-5" />,
      action: () => onScheduleGame?.(),
      variant: 'outline'
    },
    {
      title: 'Manage Players',
      description: 'Update roster',
      icon: <Users className="h-5 w-5" />,
      action: () => onManagePlayers?.(),
      variant: 'outline'
    }
  ];

  const teamOwnerActions: QuickAction[] = [
    {
      title: 'Schedule Game',
      description: 'Plan team event',
      icon: <Calendar className="h-5 w-5" />,
      action: () => onScheduleGame?.(),
      variant: 'default'
    },
    {
      title: 'Create Tournament',
      description: 'Organize competition',
      icon: <Trophy className="h-5 w-5" />,
      action: () => navigate('/tournaments'),
      variant: 'secondary'
    },
    {
      title: 'Team Message',
      description: 'Contact members',
      icon: <MessageSquare className="h-5 w-5" />,
      action: () => navigate('/lockeroom'),
      variant: 'outline'
    },
    {
      title: 'Manage Roster',
      description: 'Update team members',
      icon: <Users className="h-5 w-5" />,
      action: () => onManagePlayers?.(),
      variant: 'outline'
    }
  ];

  const venueActions: QuickAction[] = [
    {
      title: 'Create Event',
      description: 'Schedule venue event',
      icon: <Plus className="h-5 w-5" />,
      action: () => onCreateEvent?.(),
      variant: 'default'
    },
    {
      title: 'Manage Bookings',
      description: 'View reservations',
      icon: <Calendar className="h-5 w-5" />,
      action: () => onManageBookings?.(),
      variant: 'secondary'
    },
    {
      title: 'Submit Game',
      description: 'Add game to schedule',
      icon: <Trophy className="h-5 w-5" />,
      action: () => navigate('/venue-tools?tab=games'),
      variant: 'outline'
    },
    {
      title: 'View Analytics',
      description: 'Check performance',
      icon: <FileText className="h-5 w-5" />,
      action: () => navigate('/venue-tools?tab=analytics'),
      variant: 'outline'
    }
  ];

  const actions = 
    userRole === 'coach' ? coachActions :
    userRole === 'team-owner' ? teamOwnerActions :
    venueActions;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {actions.map((action, index) => (
        <Card 
          key={index} 
          className="bg-card border-border hover:border-primary transition-colors cursor-pointer"
          onClick={action.action}
        >
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                {action.icon}
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-1">{action.title}</h3>
                <p className="text-xs text-muted-foreground">{action.description}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
